/**
 * "Pay now (UPI, card, net banking)": PayU's hosted checkout (V068). The
 * server prices the plan and signs the form; this only posts it to PayU.
 * Card, UPI and bank details are typed on PayU's page, never ours.
 */
import { ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";

import { brand } from "@/brand";
import { Button, Field, Input } from "@/components/ui";
import { track } from "@/lib/analytics";
import { formatINR } from "@/lib/format";

import type { PlanDue } from "../account/platform";
import { goToPayU, type PayUStart, type PayUStartBody } from "./lib";

export function PayOnline({
  plan,
  phoneNeeded = false,
  start,
  go = goToPayU,
}: {
  plan: PlanDue;
  phoneNeeded?: boolean;
  start: (body: PayUStartBody) => Promise<PayUStart>;
  /** Tests replace the hand-over to PayU. */
  go?: (s: PayUStart) => void;
}) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const digits = phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (phoneNeeded && !/^[6-9]\d{9}$/.test(digits)) return setError("Enter your 10-digit mobile number.");
    setBusy(true);
    try {
      const s = await start({ plan_code: plan.code, months: 1, ...(phoneNeeded ? { phone: digits } : {}) });
      track("payment_online_started", { plan: plan.code, value: plan.total_amount, currency: "INR" });
      go(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start the payment. Try again.");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-3 rounded-lg border border-brand/40 bg-brand-soft/40 p-3" aria-label="Pay online" noValidate>
      <div className="grid gap-1">
        <p className="text-sm font-semibold text-fg">Pay online</p>
        <p className="text-xs text-muted">
          Pay {formatINR(plan.total_amount)} on PayU's secure page by UPI, debit or credit card, or net banking. Your
          plan is active as soon as the payment goes through.
        </p>
      </div>
      {phoneNeeded ? (
        <Field label="Mobile number" htmlFor="payu-phone" hint="PayU asks for it">
          <Input
            id="payu-phone"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit mobile"
          />
        </Field>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-lg bg-down-soft px-3 py-2 text-sm break-words text-down">
          {error}
        </p>
      ) : null}
      <Button type="submit" size="lg" loading={busy} className="w-full">
        Pay now (UPI, card, net banking)
      </Button>
      <p className="flex items-start gap-1.5 text-2xs text-subtle">
        <ShieldCheck className="mt-px size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0">
          You'll go to PayU and come back to {brand.name}. We never see your card or bank details.
        </span>
      </p>
    </form>
  );
}
